import { useCallback, useRef } from 'react';
import { DataGrid, useGridConfig } from '@eo/shared';

import type { FinishedTask } from './api/types';
import { ClientForecast } from './ClientForecast';
import { clientDeliveryRows } from './helpers';
import { TeamDelivery } from './TeamDelivery';
import {
  makeClientMembersGridConfig,
  makeFinishedTasksGridConfig,
  type ClientMemberRow,
} from './config/gridConfig';
import type { ClientSummaryProps } from './types';

// What the client's row in the table cannot show: where its month is heading,
// who worked on it, and how its finished tasks compared with their estimates.
export function ClientSummary({ report, clientId }: ClientSummaryProps) {
  const client = report.clients.find((c) => c.clientId === clientId) ?? null;
  const staffRef = useRef(report.users);
  staffRef.current = report.users;
  const staffName = useCallback(
    (userId: string) => staffRef.current.find((s) => s.userId === userId)?.name ?? userId,
    [],
  );
  const membersConfig = useGridConfig(makeClientMembersGridConfig, { staffName });
  const finishedConfig = useGridConfig(makeFinishedTasksGridConfig, undefined);

  if (!client) {
    return <p className="text-sm text-text-tertiary">This client is not in the portfolio.</p>;
  }

  return (
    <section className="flex flex-col gap-4">
      <TeamDelivery testId="client-delivery" rows={clientDeliveryRows(client, report.users)} />
      <ClientForecast forecast={client.forecast} />
      <DataGrid<ClientMemberRow> config={membersConfig} rows={client.assignments} />
      <DataGrid<FinishedTask> config={finishedConfig} rows={client.finishedTasks} />
    </section>
  );
}
